
import Vue, { PropType } from 'vue'
import { replaceCallbotMaterial, uploadCallbotMaterial } from '@/api/callbot'
import { CallbotMaterial } from '@/types/callbot'
import ZaIcon from './ZaIcon.vue'
import {
    AUDIO_READ_ERROR,
    MATERIAL_ACCEPT,
    MATERIAL_FORMAT_HINT,
    MATERIAL_NAME_MAX_LENGTH,
    defaultMaterialName,
    formatMaterialDuration,
    formatMaterialSize,
    precheckAudioFile,
    readAudioDurationInBrowser,
    validateAudioDuration,
} from './audioLibrary'

export default Vue.extend({
    name: 'UploadMaterialDialog',
    components: { ZaIcon },
    props: {
        value: { type: Boolean, default: false },
        projectId: { type: String, required: true },
        // Отклонённый ролик, который исправляем; null — новая загрузка.
        replace: { type: Object as PropType<CallbotMaterial | null>, default: null },
    },
    data() {
        return {
            name: '',
            file: null as File | null,
            fileUrl: '',
            fileDuration: 0,
            checking: false,
            fileError: '',
            submitError: '',
            busy: false,
            dragover: false,
            serial: 0,
            nameMaxLength: MATERIAL_NAME_MAX_LENGTH,
            accept: MATERIAL_ACCEPT,
            formatHint: MATERIAL_FORMAT_HINT,
        }
    },
    computed: {
        canSubmit(): boolean {
            return !!this.name.trim() && !!this.file && !this.busy && !this.checking
        },
        submitLabel(): string {
            if (this.busy) return 'Отправляем…'
            return this.replace ? 'Отправить повторно' : 'Отправить на модерацию'
        },
        fileMeta(): string {
            return this.file
                ? `${formatMaterialDuration(this.fileDuration)} · ${formatMaterialSize(this.file.size)}`
                : ''
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(open: boolean) {
                if (open) this.reset()
            },
        },
    },
    beforeDestroy() {
        this.revokeUrl()
    },
    methods: {
        reset() {
            this.serial += 1
            this.revokeUrl()
            this.name = this.replace ? this.replace.name : ''
            this.file = null
            this.fileDuration = 0
            this.checking = false
            this.fileError = ''
            this.submitError = ''
            this.busy = false
            this.dragover = false
        },
        revokeUrl() {
            if (this.fileUrl) URL.revokeObjectURL(this.fileUrl)
            this.fileUrl = ''
        },
        pick() {
            if (this.busy) return
            const input = this.$refs.fileInput as HTMLInputElement
            input.value = ''
            input.click()
        },
        onPicked(event: Event) {
            const file = (event.target as HTMLInputElement).files?.[0]
            if (file) this.readFile(file)
        },
        onDrop(event: DragEvent) {
            this.dragover = false
            const file = event.dataTransfer?.files?.[0]
            if (file) this.readFile(file)
        },
        async readFile(file: File) {
            if (this.busy) return
            const serial = ++this.serial
            this.file = null
            this.fileError = ''
            this.submitError = ''
            this.revokeUrl()

            const precheck = precheckAudioFile(file)
            if (precheck) {
                this.fileError = precheck
                return
            }
            const url = URL.createObjectURL(file)
            this.fileUrl = url
            this.checking = true
            try {
                const duration = await readAudioDurationInBrowser(url)
                if (serial !== this.serial) return
                const durationError = validateAudioDuration(duration)
                if (durationError) {
                    this.fileError = durationError
                    return
                }
                this.file = file
                this.fileDuration = duration
                if (!this.name.trim()) this.name = defaultMaterialName(file.name)
            } catch {
                if (serial === this.serial) this.fileError = AUDIO_READ_ERROR
            } finally {
                if (serial === this.serial) this.checking = false
            }
        },
        async submit() {
            if (!this.canSubmit || !this.file) return
            this.busy = true
            this.submitError = ''
            const name = this.name.trim()
            const result = this.replace
                ? await replaceCallbotMaterial(this.projectId, this.replace.id, name, this.file)
                : await uploadCallbotMaterial(this.projectId, name, this.file)
            this.busy = false
            if (result.data) {
                this.$emit('saved', result.data)
                this.$emit('input', false)
                return
            }
            this.submitError = this.errorText(result.status, result.error)
        },
        errorText(status: number, error: string): string {
            if (status === 409 && this.replace && /rejected/.test(error)) {
                return 'Этот ролик уже нельзя заменить. Загрузите новую запись.'
            }
            if (status === 409) return 'Эта запись уже есть в библиотеке. Выберите другой файл.'
            if (status === 400) return AUDIO_READ_ERROR
            return 'Не удалось отправить запись. Проверьте подключение и повторите отправку.'
        },
        onDialogInput(open: boolean) {
            if (!open) this.close()
        },
        close() {
            if (this.busy) return
            this.$emit('input', false)
        },
    },
})
